/** @import { Viewer3DImpl } from "./Viewer3DImpl" */

// We consider a frame an outlier, if it takes more than  OUTLIER_THRESHOLD_FACTOR times the
// the previous frame times (the actual threshold is dynamically adjusted every frame).
const OUTLIER_THRESHOLD_FACTOR = 2.3;

// We allow a few successive outliers, because it is quite common that a few frames are outliers,
// e.g. when garbage collection is performed.
const SUCCESSIVE_OUTLIER_LIMIT = 3;

/**
 * @typedef {Object} ViewerRenderbudgetStatus
 * @property {number} priority                 - The priority of the viewer.
 * @property {number} frameTimeFraction        - The fraction of the frame time that can be used by this viewer
 * @property {number|undefined} usedCpuTime    - The CPU time used by this viewer
 * @property {boolean} isDone                  - Whether the viewer has finished rendering
 * @property {number} desiredFrameTime         - The desired frame time for this viewer
 *
 * @private
 */


class RenderBudgetManager {
  /** @type {Map<Viewer3DImpl, ViewerRenderbudgetStatus>} */
  viewers = new Map();

  /** Scaling factor for the frame time budget */
  frameTimeScalingFactorAvg = 1.0;
  cpuTimeAverage = 1.0;
  frameTimeAverage = 1.0;


  /** Outlier tracking variables */
  outlierLimit = undefined;
  outlierCounter = 0;

  constructor() {
  }

  /**
   * Registers a new viewer with the manager.
   * @param {Viewer3DImpl} viewer - The viewer to register.
   * @param {number} priority - The priority of the viewer.
   */
  registerViewer(viewer, priority) {
    this.viewers.set(viewer, {
      priority: priority,
      frameTimeFraction: 0,
      usedCpuTime: undefined,
      desiredFrameTime: 0
    });
    this._updateFrameTimeFractions();
  }

  /**
   * Updates the priority of the given viewer.
   * @param {Viewer3DImpl} viewer - The viewer to register.
   * @param {number} priority - The priority of the viewer.
   */
  updateViewerPriority(viewer, priority) {
    this.viewers.get(viewer).priority = priority;
    this._updateFrameTimeFractions();
  }

  /**
   * Unregisters a viewer from the manager.
   * @param {Viewer3DImpl} viewer - The viewer to unregister.
   */
  unregisterViewer(viewer) {
    this.viewers.delete(viewer);
    this._updateFrameTimeFractions();
  }

  /**
   * Update the frame time fractions for all viewers.
   */
  _updateFrameTimeFractions() {
    let totalPriority = 0;
    this.viewers.forEach((viewer) => {
      totalPriority += viewer.priority;
    });

    this.viewers.forEach((viewer) => {
      viewer.frameTimeFraction = viewer.priority / totalPriority;
    });
  }

  updateFrameTime(viewer, isDone, usedCpuTime, actualFrameTime, desiredViewerFrameTime) {
    let viewerStatus = this.viewers.get(viewer);

    if (actualFrameTime === undefined) {
      viewerStatus.usedCpuTime = undefined;
      return;
    }

    // If the outlier limit is not set, we initialize it to OUTLIER_THRESHOLD_FACTOR times the actual frame time.
    this.outlierLimit ??= OUTLIER_THRESHOLD_FACTOR * actualFrameTime;

    if (actualFrameTime > this.outlierLimit) {

      // It is quite common that a few successive frames are outliers, e.g. when garbage collection
      // is performed. Therefore, we just ignore up to SUCCESSIVE_OUTLIER_LIMIT outliers
      // (time the number of viewers, because each will report a frame time) in a row. Afterwards,
      // we start increasing the outlier limit exponentially.
      this.outlierCounter++;
      if (this.outlierCounter > SUCCESSIVE_OUTLIER_LIMIT * this.viewers.size) {
        this.outlierLimit = 2 * (this.outlierLimit + 1);
      }
      viewerStatus.usedCpuTime = undefined;
      return;
    } else {
      // If the frame time is not an outlier we decrease the outlier limit
      // smoothly to OUTLIER_THRESHOLD_FACTOR the frame time of the last frame.
      this.outlierLimit = 0.8 * this.outlierLimit + 0.2 * (OUTLIER_THRESHOLD_FACTOR * actualFrameTime);
      this.outlierCounter = Math.max(0, this.outlierCounter - 1);
    }

    // We have been called a second time for the same viewer. We assume this means that a
    // frame has been presented to the user and that we now have a valid frame time and can
    // use the used cpu time to update the render time budget.
    if (usedCpuTime && this.viewers.get(viewer).usedCpuTime !== undefined) {
      let allDone = true;
      let totalUsedCpuTime = 0;
      let desiredTotalFrameTime = 0;
      let usedFrameTimeFraction = 0;
      for (let [, viewerStatus] of this.viewers) {
        if (viewerStatus.usedCpuTime !== undefined) {
          totalUsedCpuTime += viewerStatus.usedCpuTime;
          desiredTotalFrameTime += viewerStatus.desiredFrameTime;
          viewerStatus.usedCpuTime = undefined;
          usedFrameTimeFraction += viewerStatus.frameTimeFraction;
          allDone &= viewerStatus.isDone;
        }
      }

      // We don't want to trigger updates of the frame time if only a small fraction of the total render budget has
      // happened this frame (e.g. only the minimap has been redrawn). This factor is somewhat arbitrary, but it seems
      // reasonable that we should have done at least half of the total render budget to get a meaningful update.
      if (usedFrameTimeFraction > 0.5) {

        // The factors determines how slow/fast we adjust to changing frame times.
        const generalIncFactor = 0.8;
        const inverseIncFactor = 1.0 - generalIncFactor;

        // If all viewers have completed rendering (allDone is true), we do not
        // know the ratio between CPU and GPU time (since rendering have completed in
        // less than one frame time). In that case, we set the ratio to 1.0 to avoid underestimating
        // the frame time scaling factor (e.g. if we rendered a tiny scene in 1ms and it took 16 ms
        // until the next frame is presented we would estimate the factor as 1/16, even though there was
        // no large GPU overhead). Such an underestimation could result in unnecessarily skipping rendering
        // of geometry for simple scenes.
        if (allDone && actualFrameTime < desiredTotalFrameTime) {
          totalUsedCpuTime = actualFrameTime = 1.0;
        }
        this.cpuTimeAverage = generalIncFactor * this.cpuTimeAverage + inverseIncFactor * totalUsedCpuTime / usedFrameTimeFraction;
        this.frameTimeAverage = generalIncFactor * this.frameTimeAverage + inverseIncFactor * actualFrameTime / usedFrameTimeFraction;

        this.frameTimeScalingFactorAvg = this.cpuTimeAverage / this.frameTimeAverage;

        for (let [viewerInstance, viewerStatus] of this.viewers) {
          viewerInstance._updateTargetFrameBudget(this.frameTimeScalingFactorAvg, viewerStatus.frameTimeFraction);
        }
      }
    }

    viewerStatus.usedCpuTime = usedCpuTime;
    viewerStatus.isDone = isDone;
    viewerStatus.desiredFrameTime = desiredViewerFrameTime;
  }
}


export let renderBudgetManager = new RenderBudgetManager();